import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface AppLoaderProps {
  /** Whether the app is still loading */
  isLoading: boolean;
  /** Optional message to display */
  message?: string;
  /** Minimum display time in ms (prevents flash) */
  minDisplayTime?: number;
}

/**
 * A branded full-screen loader with smooth fade transitions.
 * Used during authentication and initial data loading.
 */
export function AppLoader({ 
  isLoading, 
  message = 'Even laden...', 
  minDisplayTime = 300 
}: AppLoaderProps) {
  const [visible, setVisible] = useState(true);
  const [shouldRender, setShouldRender] = useState(true);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    if (!isLoading) {
      // Ensure minimum display time for smooth UX
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);
      
      const hideTimer = setTimeout(() => {
        setVisible(false);
      }, remaining);

      return () => clearTimeout(hideTimer);
    }
  }, [isLoading, startTime, minDisplayTime]);

  // Remove from DOM after fade-out animation completes
  useEffect(() => {
    if (!visible) {
      const removeTimer = setTimeout(() => {
        setShouldRender(false);
      }, 400); // Match CSS transition duration
      return () => clearTimeout(removeTimer);
    }
  }, [visible]);

  if (!shouldRender) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center justify-center bg-background transition-opacity duration-400",
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
      style={{ transitionDuration: '400ms' }}
    >
      {/* Logo with pulse animation */}
      <div className="relative mb-6">
        <svg 
          className="w-16 h-16 animate-pulse" 
          viewBox="0 0 64 64" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="32" cy="32" r="28" className="fill-primary/15" />
          <circle cx="32" cy="32" r="20" className="fill-primary/30" />
          <path 
            d="M24 32L30 38L40 26" 
            className="stroke-primary" 
            strokeWidth="3" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Spinner */}
      <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" 
           style={{ borderWidth: '3px' }} />

      {/* Loading message */}
      <p className="mt-4 font-display text-sm text-muted-foreground tracking-wide">
        {message}
      </p>
    </div>
  );
}
